<template>
  <div style="height: 100%; display: flex; flex-direction: column">
    <div class="d-flex align-center pa-4 pb-2 justify-space-between">
      <div class="text-subtitle-1 font-weight-bold">
        Scenes Using This Asset
        <v-chip
          size="x-small"
          v-if="usageList.length"
          class="ml-2">
          {{ usageList.length }}
        </v-chip>
      </div>
      <v-btn
        v-if="usageList.length > 0"
        color="error"
        variant="text"
        size="small"
        prepend-icon="mdi-delete-sweep"
        :loading="deleting"
        @click="deleteAllScenes">
        Delete All
      </v-btn>
    </div>

    <div
      class="pa-4"
      v-if="loading">
      <v-progress-linear
        indeterminate
        color="primary"></v-progress-linear>
      <div class="text-center mt-2">Checking usage...</div>
    </div>

    <div
      v-else-if="usageList.length === 0"
      class="d-flex align-center justify-center flex-grow-1">
      <div class="text-center text-grey">
        <v-icon
          size="64"
          class="mb-2">
          mdi-link-variant-off
        </v-icon>
        <div class="text-h6">No usage found</div>
        <div class="text-body-2">
          This asset is not currently used in any Scenes or Templates
        </div>
      </div>
    </div>

    <v-list
      v-else
      style="background: transparent; overflow-y: auto"
      lines="two"
      class="px-2">
      <v-list-item
        v-for="(item, index) in usageList"
        :key="index"
        :prepend-icon="item.icon"
        :title="item.name || 'Unnamed'">
        <template v-slot:append>
          <v-chip
            size="small"
            variant="outlined"
            class="ml-2 mr-2">
            {{ item.type }}
          </v-chip>
          <v-btn
            icon="mdi-delete"
            variant="text"
            size="small"
            color="error"
            @click="deleteScene(item.uid)"></v-btn>
        </template>
        <div
          class="text-caption text-grey"
          v-if="item.description">
          {{ item.description }}
        </div>
        <div
          class="text-caption text-grey copy-cursor"
          @click="copy(item.uid)">
          {{ item.uid }}
        </div>
      </v-list-item>
    </v-list>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { $http } from '../../../stores/http';

const props = defineProps(['userEditObj']);
const loading = ref(true);
const usageList = ref([]);
const deleting = ref(false);

async function deleteScene(uid) {
  if (!confirm('Are you sure you want to delete this scene?')) return;

  try {
    const res = await $http(
      'DELETE',
      `/api/dynamic_engine/delete_scene?scene_uid=${uid}`
    );
    if (res) {
      usageList.value = usageList.value.filter((item) => item.uid !== uid);
    }
  } catch (e) {
    console.error(e);
    alert('Failed to delete scene');
  }
}

async function deleteAllScenes() {
  if (
    !confirm(
      `Are you sure you want to delete ALL ${usageList.value.length} scenes using this asset? This cannot be undone.`
    )
  )
    return;

  deleting.value = true;
  try {
    // We delete one by one to handle errors gracefully and update UI progressively if needed
    // or we could add a bulk delete endpoint. For now, client-side loop is safer.
    const itemsToDelete = [...usageList.value];
    for (const item of itemsToDelete) {
      try {
        await $http(
          'DELETE',
          `/api/dynamic_engine/delete_scene?scene_uid=${item.uid}`
        );
        usageList.value = usageList.value.filter((i) => i.uid !== item.uid);
      } catch (err) {
        console.error(`Failed to delete scene ${item.uid}`, err);
      }
    }
    if (usageList.value.length === 0) {
      alert('All scenes deleted successfully');
    } else {
      alert('Some scenes could not be deleted. Check console for details.');
    }
  } catch (e) {
    console.error(e);
    alert('Error during batch delete');
  } finally {
    deleting.value = false;
  }
}

function copy(text) {
  navigator.clipboard.writeText(text);
}

onMounted(async () => {
  if (!props.userEditObj || !props.userEditObj.uid) return;

  try {
    const res = await $http(
      'GET',
      `/api/dynamic_engine/get_asset_usage/${props.userEditObj.uid}`
    );
    if (res) {
      usageList.value = JSON.parse(res);
    }
  } catch (e) {
    console.error('Failed to get asset usage', e);
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.copy-cursor {
  cursor: pointer;
}
.copy-cursor:hover {
  text-decoration: underline;
}
</style>
